import i18next from "i18next";
import React, { useEffect, useState } from "react";
import { FIELD_REQUIRED, LOADING } from "../../i18n/keysTranslations";
import { Controller, useWatch, useController } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../lib/i18next";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useDispatchApp } from "../../lib/redux";
import { startGetTrackingCustomEvents } from "../../actions/adsCampaigns";
import { LINKEDIN } from "../../utils/constants";
import _ from "lodash";
import { getUniqueID } from "../../utils/numbers";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../utils/colors";

const filter = createFilterOptions();

const SelectConversionActionsLinkedin = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  integrationID,
  adAccountID,
  objective,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  sxTextField = {},
  onChangeAction = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();

  // Get current field value
  const currentValue = useWatch({
    control,
    name,
    defaultValue: [],
  });

  // Get field controller to update value
  const {
    field: { onChange: setFieldValue },
  } = useController({
    control,
    name,
    defaultValue: [],
  });

  const getInitialData = async () => {
    if (!integrationID || !adAccountID) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const response = await dispatch(
      startGetTrackingCustomEvents({
        adAccountID,
        integrationID,
        platform: LINKEDIN,
        objective,
      })
    );

    if (response?.ok && response?.data?.length > 0) {
      setOptions(response.data);
    } else {
      setOptions([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line
  }, [adAccountID, integrationID]);

  // Filter options internally based on objective
  const filteredOptions = React.useMemo(() => {
    if (objective === "leads") {
      return options.filter((item) => item.type === "QUALIFIED_LEAD");
    }
    return options;
  }, [options, objective]);

  // Filter selected values when objective changes to "leads"
  useEffect(() => {
    if (objective === "leads" && currentValue && currentValue.length > 0) {
      const filteredValues = currentValue.filter(
        (item) => item.type === "QUALIFIED_LEAD"
      );

      // Only update if values were filtered out
      if (filteredValues.length !== currentValue.length) {
        setFieldValue(filteredValues);
        onChangeAction(filteredValues);
      }
    }
    // eslint-disable-next-line
  }, [objective]);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={[]}
      render={({ field }) => (
        <>
          <Autocomplete
            {...field}
            id={name}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading}
            open={open}
            multiple={true}
            value={field.value || []}
            onOpen={() => {
              if (!field.value || _.isEmpty(field.value)) field.onChange([]);
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={(_, value) => {
              field.onChange(value);
              onChangeAction(value);
            }}
            isOptionEqualToValue={(option, value) => {
              return option?.id === value?.id;
            }}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option ? t(option) : "";
              }
              return option?.name || "";
            }}
            options={filteredOptions}
            disableClearable={true}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);

              return filtered;
            }}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  key={option.id || getUniqueID()}
                  component="li"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1">
                    {option.name} ({option.id})
                  </Typography>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id || getUniqueID()}
                  label={option.name}
                  {...getTagProps({ index })}
                  sx={{
                    backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant={variant}
                placeholder={field?.value?.length > 0 ? "" : placeholder}
                hiddenLabel={!label}
                error={!!error}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                  ...sxTextField,
                  ".MuiFormHelperText-root": {
                    ml: "0 !important",
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          {(error?.message || helperText) && (
            <Typography
              variant="caption"
              color={error?.message ? "error" : "fields.placeholder"}
            >
              {error?.message || helperText}
            </Typography>
          )}
        </>
      )}
    />
  );
};

export default SelectConversionActionsLinkedin;
